import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getJSON, postJSON } from '../../api/client.js';
import { WABA } from '../../config';
import {
  THEME, UniActionButton, UniPageShell, UniStatusPill, WABA_API, WABA_V1,
  wabaHeaders,
} from './WabaShared.jsx';

export default function WabaInbox() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const activeId = conversationId ? Number(conversationId) : null;

  const [numbers, setNumbers] = useState([]);
  const [current, setCurrent] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('open');
  const [body, setBody] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [windowOpen, setWindowOpen] = useState(true);
  const lastIdRef = useRef(0);
  const bottomRef = useRef(null);
  const phone = current?.phone_number;

  const loadNumbers = useCallback(async () => {
    try {
      const data = await getJSON(`${WABA_API}/configs/current/`);
      setCurrent(data.current);
      setNumbers(data.numbers || []);
    } catch {
      const list = await getJSON(`${WABA_API}/configs/`);
      const rows = Array.isArray(list) ? list : list.results || [];
      setNumbers(rows);
      setCurrent(rows.find((n) => n.is_default) || rows[0] || null);
    }
  }, []);

  const loadConversations = useCallback(async () => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (search) params.set('search', search);
    if (current?.id) params.set('config', current.id);
    try {
      const data = await getJSON(`${WABA_V1}/conversations/?${params}`, wabaHeaders(phone));
      setConversations(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      setError(e?.message || 'Could not load conversations.');
    }
  }, [status, search, current, phone]);

  useEffect(() => { loadNumbers(); }, [loadNumbers]);
  useEffect(() => { loadConversations(); }, [loadConversations]);

  useEffect(() => {
    setMessages([]);
    lastIdRef.current = 0;
    if (!activeId) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const data = await getJSON(
          `${WABA_V1}/conversations/${activeId}/messages/?since=${lastIdRef.current || ''}`,
          wabaHeaders(phone),
        );
        const incoming = Array.isArray(data) ? data : data.results || [];
        if (cancelled || !incoming.length) return;
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          const extra = incoming.filter((m) => !seen.has(m.id));
          return extra.length ? [...prev, ...extra] : prev;
        });
        lastIdRef.current = Math.max(
          lastIdRef.current,
          ...incoming.map((m) => m.id),
        );
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Could not load messages.');
      }
    };
    tick();
    const timer = setInterval(tick, WABA.POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [activeId, phone]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  useEffect(() => {
    if (!activeId) return;
    getJSON(`${WABA_V1}/conversations/${activeId}/`, wabaHeaders(phone))
      .then((c) => setWindowOpen(c.is_window_open !== false))
      .catch(() => {});
  }, [activeId, phone]);

  const send = async () => {
    if (!activeId) return;
    setSending(true);
    setError(null);
    try {
      if (windowOpen && body.trim()) {
        await postJSON(`${WABA_V1}/messages/text/`, {
          conversation_id: activeId,
          text: body.trim(),
        }, wabaHeaders(phone));
        setBody('');
      } else if (templateName.trim()) {
        await postJSON(`${WABA_V1}/messages/template/`, {
          conversation_id: activeId,
          template_name: templateName.trim(),
        }, wabaHeaders(phone));
        setTemplateName('');
      }
    } catch (e) {
      setError(e?.message || 'Send failed. Outside the 24h window you must use an approved template.');
    } finally {
      setSending(false);
    }
  };

  const act = async (actionName, payload = {}) => {
    if (!activeId) return;
    try {
      await postJSON(`${WABA_API}/conversations/${activeId}/${actionName}/`, payload);
      loadConversations();
    } catch (e) {
      setError(e?.message || 'Action failed.');
    }
  };

  const active = conversations.find((c) => c.id === activeId);

  return (
    <UniPageShell
      theme={THEME}
      icon="💬"
      title="WhatsApp inbox"
      subtitle="Live customer chats. Help on this page matches the mobile inbox."
      helpPage="inbox"
      helpApi={WABA_API}
      actions={<UniActionButton hero to="/waba-chat">← Home</UniActionButton>}
    >
      {error && <div className="ty-error" style={{ marginBottom: 12 }}>{error}</div>}
      <div className="waba-inbox">
        <aside className="waba-inbox__list">
          <div className="waba-inbox__list-head">
            <select
              value={current?.id || ''}
              onChange={async (e) => {
                const id = e.target.value;
                const row = numbers.find((n) => String(n.id) === String(id));
                setCurrent(row || null);
                if (id) {
                  try { await postJSON(`${WABA_API}/configs/switch/`, { config_id: id }); } catch { /* session optional */ }
                }
              }}
              style={{ flex: 1, padding: 6 }}
            >
              {numbers.map((n) => (
                <option key={n.id} value={n.id}>{n.business_name || n.phone_number}</option>
              ))}
              {numbers.length === 0 && <option value="">No numbers — add one in Settings</option>}
            </select>
            <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ padding: 6 }}>
              <option value="">All</option>
              <option value="open">Open</option>
              <option value="pending">Pending</option>
              <option value="resolved">Resolved</option>
              <option value="snoozed">Snoozed</option>
            </select>
          </div>
          <input
            placeholder="Search name or phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ margin: 10, padding: 8, borderRadius: 8, border: '1px solid #e5e7eb' }}
          />
          <div style={{ overflow: 'auto', flex: 1 }}>
            {conversations.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`waba-conv${c.id === activeId ? ' waba-conv--active' : ''}`}
                onClick={() => navigate(`/waba-chat/inbox/${c.id}`)}
              >
                <div className="waba-conv__name">
                  {c.contact_name || c.contact_phone || 'Unknown'}
                  {c.unread_count > 0 && (
                    <UniStatusPill value={`${c.unread_count} new`} palette="green" />
                  )}
                </div>
                <div className="waba-conv__meta">
                  {c.last_message_preview || c.contact_phone} · {c.status}
                  {c.is_window_open === false ? ' · window closed' : ''}
                </div>
              </button>
            ))}
            {conversations.length === 0 && (
              <div className="uni-state"><div className="uni-state__title">No conversations</div></div>
            )}
          </div>
        </aside>
        <section className="waba-inbox__thread">
          {!activeId ? (
            <div className="uni-state"><div className="uni-state__title">Pick a conversation</div></div>
          ) : (
            <>
              <div className="waba-inbox__thread-head">
                <div style={{ flex: 1 }}>
                  <strong>{active?.contact_name || active?.contact_phone || `#${activeId}`}</strong>
                  <div className="uni-num--muted" style={{ fontSize: 12 }}>
                    {windowOpen ? '24-hour window open — free-form text OK' : 'Window closed — send an approved template'}
                  </div>
                </div>
                <UniActionButton onClick={() => act('mark-done')}>Mark done</UniActionButton>
                <UniActionButton onClick={() => act('reopen')}>Reopen</UniActionButton>
              </div>
              <div className="waba-bubbles">
                {messages.map((m) => (
                  <div
                    key={m.id}
                    className={`waba-bubble ${m.sender_type === 'customer' ? 'waba-bubble--in' : 'waba-bubble--out'}`}
                  >
                    <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>
                      {m.sender_name || m.sender_type} · {m.status}
                    </div>
                    {m.content || m.media_caption || (m.is_template ? `Template ${m.template_name}` : '')}
                    {m.media_url && (
                      <div><a href={m.media_url} target="_blank" rel="noreferrer">Media</a></div>
                    )}
                    {m.ai_response && (
                      <div style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}>
                        AI suggestion ({m.ai_confidence ?? '—'}): {m.ai_response}
                      </div>
                    )}
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <div className="waba-compose">
                {windowOpen ? (
                  <textarea
                    rows={2}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Type a reply…"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                    }}
                  />
                ) : (
                  <input
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Approved template name"
                  />
                )}
                <button type="button" className="ty-btn" disabled={sending} onClick={send}>
                  {sending ? '…' : 'Send'}
                </button>
              </div>
            </>
          )}
        </section>
      </div>
    </UniPageShell>
  );
}
